import { CSSProperties, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, Variants } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { AgentMode, useAgentContext } from '@Providers';

import { sendCustomEvent } from '@Analytics';
import { TooltipProvider } from '@Components/ui-v2/tooltip';
import { AGENT_DURATIONS, AGENT_EASING } from '../../../Helpers/Agents/animations';
import { claimAbandonReport } from '../../../Helpers/Agents/lastAnswer';
import { getStorageItem, setStorageItem } from '../../../Helpers/Agents/storage';
import Header from '../Chrome/Header';
import NavSidebar from '../Chrome/NavSidebar';
import ConversationView from '../Conversation/ConversationView';

const SIDEBAR_WIDTH_KEY = 'bops.agent.sidebarWidth';
const DEFAULT_SIDEBAR_WIDTH = 460;
const MIN_SIDEBAR_WIDTH = 380;
const MAX_SIDEBAR_WIDTH = 900;
const SIDEBAR_WIDTH_CSS_VAR = '--bops-agent-sidebar-width';
const NAV_WIDTH = 272;

const readPersistedWidth = (): number => {
  const saved = getStorageItem(SIDEBAR_WIDTH_KEY);
  const parsed = saved ? parseInt(saved, 10) : DEFAULT_SIDEBAR_WIDTH;
  if (!Number.isFinite(parsed)) return DEFAULT_SIDEBAR_WIDTH;
  return Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, parsed));
};

const getLayoutClasses = (mode: AgentMode): string => {
  if (mode === 'sidebar') {
    return 'fixed right-0 top-0 z-30 h-full border-l border-gray-200 shadow-lg isolate';
  }
  if (mode === 'floating') {
    return 'fixed bottom-4 right-4 z-[101] flex flex-col overflow-hidden rounded-2xl border border-gray-200 shadow-2xl';
  }
  return 'fixed inset-0 z-[101] flex';
};

// initial/exit are evaluated once at mount/unmount — animations track the mode at that moment.
const getEntryVariants = (mode: AgentMode): Variants => {
  if (mode === 'sidebar') {
    return {
      initial: { opacity: 0, x: 20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: 20 },
    };
  }
  if (mode === 'floating') {
    return {
      initial: { opacity: 0, scale: 0.96, y: 8 },
      animate: { opacity: 1, scale: 1, y: 0 },
      exit: { opacity: 0, scale: 0.96, y: 8 },
    };
  }
  return {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };
};

const getLayoutStyle = (mode: AgentMode, sidebarWidth: number): CSSProperties => {
  if (mode === 'sidebar') {
    // Clamp to the viewport so a narrow screen never lets the right-anchored panel
    // overflow off the left edge.
    return { width: `min(${sidebarWidth}px, 100vw)`, backgroundColor: '#ffffff' };
  }
  if (mode === 'floating') {
    return {
      width: 'min(420px, calc(100vw - 24px))',
      height: 'min(640px, calc(100vh - 32px))',
      backgroundColor: '#ffffff',
    };
  }
  return { backgroundColor: '#ffffff' };
};

// Stable key across mode changes preserves ConversationView (scroll/wizard state) on minimize/maximize.
const AgentShell = () => {
  const { t } = useTranslation();
  const { mode, isNavSidebarCollapsed } = useAgentContext();

  const [sidebarWidth, setSidebarWidth] = useState<number>(readPersistedWidth);
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const latestWidthRef = useRef(sidebarWidth);
  latestWidthRef.current = sidebarWidth;

  useEffect(() => {
    document.documentElement.style.setProperty(SIDEBAR_WIDTH_CSS_VAR, `${sidebarWidth}px`);
  }, [sidebarWidth]);

  useEffect(
    () => () => {
      document.documentElement.style.removeProperty(SIDEBAR_WIDTH_CSS_VAR);
    },
    [],
  );

  // Left right after an answer without touching it. RUM only (D8).
  useEffect(() => {
    const report = (trigger: 'hidden' | 'closed') => {
      const answer = claimAbandonReport(Date.now());
      if (answer) sendCustomEvent('AGENT_SESSION_ABANDON', { conversationId: answer.conversationId, trigger });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') report('hidden');
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      // AgentRoot unmounts the shell when the panel closes.
      report('closed');
    };
  }, []);

  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    setIsResizing(true);
    document.body.style.userSelect = 'none';

    const iframes = document.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      // eslint-disable-next-line no-param-reassign
      iframe.style.pointerEvents = 'none';
    });

    const onMove = (event: MouseEvent) => {
      if (!isResizingRef.current) return;
      const next = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, window.innerWidth - event.clientX));
      setSidebarWidth(next);
    };

    const onUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      setIsResizing(false);
      document.body.style.userSelect = '';
      iframes.forEach((iframe) => {
        // eslint-disable-next-line no-param-reassign
        iframe.style.pointerEvents = '';
      });
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setStorageItem(SIDEBAR_WIDTH_KEY, String(latestWidthRef.current));
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const entryVariants = getEntryVariants(mode);

  return (
    <TooltipProvider delayDuration={250}>
      <motion.div
        key="agent-shell"
        layout={!isResizing}
        initial={entryVariants.initial}
        animate={entryVariants.animate}
        exit={entryVariants.exit}
        transition={{ layout: { duration: 0.18, ease: [0, 0, 0.2, 1] } }}
        className={getLayoutClasses(mode)}
        style={getLayoutStyle(mode, sidebarWidth)}>
        {mode === 'sidebar' && (
          <div
            onMouseDown={startResizing}
            title="Resize"
            aria-label={t('Agent.AriaResizeSidebar')}
            className="absolute left-0 top-0 z-10 h-full w-[2px] cursor-ew-resize select-none bg-gray-100 hover:bg-[#7c6cf6]/40"
          />
        )}

        {mode === 'fullscreen' && (
          <AnimatePresence initial={false}>
            {!isNavSidebarCollapsed && (
              <motion.aside
                key="nav-sidebar"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: NAV_WIDTH, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: AGENT_DURATIONS.base, ease: AGENT_EASING.outExpo }}
                className="flex h-full flex-col overflow-hidden border-r border-gray-200"
                style={{ backgroundColor: 'transparent' }}>
                <NavSidebar />
              </motion.aside>
            )}
          </AnimatePresence>
        )}

        <div className="flex h-full min-w-0 flex-1 flex-col">
          <Header />
          <div className="flex-1 overflow-hidden">
            <ConversationView />
          </div>
        </div>
      </motion.div>
    </TooltipProvider>
  );
};

export default AgentShell;
